import type { JSX, ReactNode } from 'react'
import { Link as RouterLink } from 'react-router-dom'
import Box from '@mui/material/Box'
import Card from '@mui/material/Card'
import CardContent from '@mui/material/CardContent'
import Stack from '@mui/material/Stack'
import Tooltip from '@mui/material/Tooltip'
import Typography from '@mui/material/Typography'
import { EM_DASH, formatNumber } from '../format'

/**
 * The numbers on the front page, and the bars that break them down.
 *
 * Two forms only, chosen by what the number is FOR rather than by what would
 * look busiest:
 *
 *   - a single figure somebody acts on is a stat tile, not a chart. A chart of
 *     one number is decoration with axes.
 *   - a whole split into named parts is one horizontal bar. Not a pie: parts of
 *     a whole are compared by length far more accurately than by angle, and a
 *     pie cannot carry its own labels at this size.
 *
 * There is no time series here because the product does not store one. A
 * sparkline over invented history would be the worst thing on the page.
 *
 * Colour never carries meaning alone. Every segment has a written label and a
 * number beside it, and the colours are the SAME semantic colours the status
 * chips use, so "found" is the identical green in a bar and in a table cell.
 */

/** The semantic colours, resolved from the theme so light and dark both work. */
export type StatTone = 'neutral' | 'good' | 'warning' | 'bad' | 'info' | 'brand'

const TONE_PATH: Record<StatTone, string> = {
  neutral: 'text.disabled',
  good: 'success.main',
  warning: 'warning.main',
  bad: 'error.main',
  info: 'info.main',
  brand: 'primary.main',
}

/** A figure, a dash for nothing, or a string shown as it came. */
function statText(value: string | number | null): string {
  if (value === null) return EM_DASH
  return typeof value === 'number' ? formatNumber(value) : value
}

export function toneColor(tone: StatTone): string {
  return TONE_PATH[tone]
}

/**
 * One figure, with the one line of context that makes it mean something.
 *
 * `to` makes the whole tile a link. Every number on a dashboard raises the same
 * question — "which ones?" — and a tile that cannot answer it sends people
 * hunting through the nav for the list it came from.
 */
export function StatTile({
  label,
  value,
  unit,
  hint,
  tone = 'neutral',
  to,
  icon,
}: Readonly<{
  label: string
  value: number | string | null
  unit?: string
  hint?: ReactNode
  tone?: StatTone
  to?: string
  icon?: ReactNode
}>): JSX.Element {
  const shown = statText(value)
  const interactive = to !== undefined

  return (
    <Card
      variant="outlined"
      {...(interactive ? { component: RouterLink, to } : {})}
      sx={{
        height: '100%',
        display: 'block',
        textDecoration: 'none',
        transition: 'border-color 120ms ease, background-color 120ms ease',
        ...(interactive
          ? {
              '&:hover': { borderColor: 'primary.main', bgcolor: 'action.hover' },
              '&:focus-visible': { outline: '2px solid', outlineColor: 'primary.main', outlineOffset: 2 },
            }
          : {}),
      }}
    >
      <CardContent sx={{ p: 2, '&:last-child': { pb: 2 } }}>
        <Stack direction="row" alignItems="center" spacing={0.75} sx={{ mb: 0.5 }}>
          {icon ? <Box sx={{ display: 'flex', color: 'text.secondary' }}>{icon}</Box> : null}
          <Typography
            variant="caption"
            color="text.secondary"
            sx={{ fontWeight: 600, letterSpacing: '0.02em' }}
          >
            {label}
          </Typography>
        </Stack>
        <Stack direction="row" alignItems="baseline" spacing={0.75}>
          <Typography
            sx={{
              // Tabular figures so a column of tiles lines up and a number that
              // grows by a digit does not shift everything beside it.
              fontVariantNumeric: 'tabular-nums',
              fontSize: '1.75rem',
              fontWeight: 650,
              lineHeight: 1.1,
              letterSpacing: '-0.02em',
              color: tone === 'neutral' ? 'text.primary' : toneColor(tone),
            }}
          >
            {shown}
          </Typography>
          {unit ? (
            <Typography variant="body2" color="text.secondary">
              {unit}
            </Typography>
          ) : null}
        </Stack>
        {hint ? (
          <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.5 }}>
            {hint}
          </Typography>
        ) : null}
      </CardContent>
    </Card>
  )
}

export interface BreakdownPart {
  key: string
  label: string
  value: number
  tone: StatTone
  /** Where this slice of the data lives, so a segment can be clicked through. */
  to?: string
}

/**
 * A whole, split into named parts.
 *
 * One bar with a 2px gap between segments, then a legend that carries the label
 * and the count for every part. The legend is the thing that makes it readable:
 * the bar shows proportion at a glance and the legend answers "how many", and
 * neither on its own is enough.
 *
 * Zero-value parts are dropped from the bar and kept in the legend. A segment
 * you cannot see is not information, but "none of these are pending" is.
 */
export function Breakdown({
  parts,
  total,
  height = 10,
}: Readonly<{
  parts: BreakdownPart[]
  /** Pass explicitly when some rows are excluded, so the bar is honest about the whole. */
  total?: number
  height?: number
}>): JSX.Element {
  const sum = total ?? parts.reduce((n, p) => n + p.value, 0)
  const visible = parts.filter((p) => p.value > 0)

  return (
    <Stack spacing={1.25}>
      <Box
        role="img"
        aria-label={parts.map((p) => `${p.label}: ${p.value}`).join(', ')}
        sx={{
          display: 'flex',
          gap: '2px',
          height,
          borderRadius: 999,
          overflow: 'hidden',
          bgcolor: 'action.hover',
        }}
      >
        {sum === 0
          ? null
          : visible.map((part) => (
              <Tooltip key={part.key} title={`${part.label}: ${formatNumber(part.value)}`}>
                <Box
                  sx={{
                    flexGrow: part.value,
                    flexBasis: 0,
                    // A segment that rounds to nothing still has to be visible,
                    // or a real category disappears from a chart that claims to
                    // show all of them.
                    minWidth: 3,
                    bgcolor: toneColor(part.tone),
                  }}
                />
              </Tooltip>
            ))}
      </Box>
      <Stack direction="row" flexWrap="wrap" sx={{ gap: 0.5, rowGap: 0.75 }}>
        {parts.map((part) => (
          <LegendItem key={part.key} part={part} total={sum} />
        ))}
      </Stack>
    </Stack>
  )
}

function LegendItem({ part, total }: Readonly<{ part: BreakdownPart; total: number }>): JSX.Element {
  const share = total > 0 ? Math.round((part.value / total) * 100) : 0
  const body = (
    <Stack
      direction="row"
      alignItems="center"
      spacing={0.75}
      sx={{
        px: 1,
        py: 0.375,
        borderRadius: 1,
        minWidth: 0,
        ...(part.to
          ? {
              textDecoration: 'none',
              color: 'inherit',
              '&:hover': { bgcolor: 'action.hover' },
              '&:focus-visible': { outline: '2px solid', outlineColor: 'primary.main', outlineOffset: 1 },
            }
          : {}),
      }}
    >
      <Box sx={{ width: 8, height: 8, borderRadius: '2px', bgcolor: toneColor(part.tone), flexShrink: 0 }} />
      <Typography variant="caption" color="text.secondary" noWrap>
        {part.label}
      </Typography>
      <Typography
        variant="caption"
        sx={{ fontVariantNumeric: 'tabular-nums', fontWeight: 600, color: 'text.primary' }}
      >
        {formatNumber(part.value)}
      </Typography>
      <Typography variant="caption" color="text.disabled" sx={{ fontVariantNumeric: 'tabular-nums' }}>
        {share}%
      </Typography>
    </Stack>
  )
  return part.to ? (
    <Box component={RouterLink} to={part.to} sx={{ textDecoration: 'none' }}>
      {body}
    </Box>
  ) : (
    body
  )
}
